import React from "react";
import { useSelector } from "react-redux";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";

import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import { IRateCardProps } from "../types/rate-card";

interface DeleteRateCardDialogProps {
  deleteLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
  selectedRateCard: IRateCardProps | null;
}

export const DeleteRateCardDialog: React.FC<DeleteRateCardDialogProps> = ({
  deleteLoading,
  onClose,
  onConfirm,
  open,
  selectedRateCard,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";
  const primaryTextColor = reduxTheme.primary_colors?.[400] || "#9c6644";
  const secondaryTextColor = reduxTheme.neutral_colors?.[100] || "#D6DDEB";
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  return (
    <Dialog
      open={open}
      onClose={deleteLoading ? undefined : onClose}
      aria-labelledby="delete-rate-card-dialog-title"
      aria-describedby="delete-rate-card-dialog-description"
      PaperProps={{
        sx: {
          bgcolor: backgroundColor,
          color: primaryTextColor,
        },
      }}
    >
      <DialogTitle id="delete-rate-card-dialog-title">
        Confirm Rate Card Deletion
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          id="delete-rate-card-dialog-description"
          sx={{ color: secondaryTextColor }}
        >
          Are you sure you want to delete the rate card for{" "}
          <Typography
            component="span"
            fontWeight="bold"
            sx={{ color: primaryTextColor }}
          >
            {selectedRateCard?.role?.name ?? "this role"}
          </Typography>
          ? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={deleteLoading}
          sx={{
            color: primaryColor,
            "&:hover": { bgcolor: primaryColor, color: "#fff" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={deleteLoading}
          variant="contained"
          sx={{
            bgcolor: "error.main",
            "&:hover": { bgcolor: "error.dark" },
            color: "#fff",
          }}
          autoFocus
        >
          {deleteLoading ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : (
            "Delete"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteRateCardDialog;
